// ── The document ─────────────────────────────────────────────
/**
 * A partner's terms, stored as ProseMirror JSON rather than as HTML.
 *
 * The editor in the portal is TipTap, whose native document form this is,
 * so nothing is converted on the way in. Keeping the JSON rather than the
 * HTML it could render is what makes the rest of the feature cheap:
 *
 * - Nothing anywhere stores, serves or renders HTML, so there is no
 *   sanitiser on two read paths and no injection surface to get wrong.
 *   Validation is an allow-list of the node and mark names below — a
 *   document naming anything else is refused rather than scrubbed.
 * - React Native has no HTML renderer and `@zuruflex/mobile-ui` has no
 *   markdown library. A node tree walks into `<T>` and `<View>` exactly as
 *   readily as into `<p>` and `<div>`, so one document renders on the
 *   website, in both phone apps and in the dashboard.
 * - ProseMirror ships only to the partner portal. Every reader renders
 *   from this shape with no editor dependency at all.
 */
export const TERMS_NODE_TYPES = [
    'doc',
    'paragraph',
    'heading',
    'bulletList',
    'orderedList',
    'listItem',
    'hardBreak',
    'text',
];
/** Inline formatting. Deliberately three: emphasis, and somewhere to go. */
export const TERMS_MARK_TYPES = ['bold', 'italic', 'link'];
/**
 * Headings stop at two levels.
 *
 * The document is rendered inside a page that already owns `h1`, and a
 * clause numbered four levels deep is one nobody reads. Two is enough for
 * "3. Fuel and mileage" and a sub-clause under it.
 */
export const TERMS_HEADING_LEVELS = [2, 3];
/** An empty document, which is what an untouched editor holds. */
export const EMPTY_TERMS_DOC = { type: 'doc', content: [] };
// ── A partner's own rental terms ─────────────────────────────
/**
 * Where a version stands. The same four states a listing moves through,
 * and for the same reason: what a renter is asked to agree to is a claim
 * about the hire, so Zuruflex reads it before it goes in front of anyone.
 */
export const TERMS_STATUSES = ['DRAFT', 'IN_REVIEW', 'PUBLISHED', 'REJECTED'];
/** Which of those a partner may still edit. The other two are frozen. */
export const EDITABLE_TERMS_STATUSES = ['DRAFT', 'REJECTED'];
export const TERMS_ACCEPTANCE_SOURCES = ['WEB', 'APP', 'WHATSAPP', 'STAFF'];
